/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties } from 'react';
import { type ClassValue } from 'clsx';

import { omitUndefined } from 'utils/object';
import { ensureArray } from 'utils/array';

import { resolveColor } from './colors';
import { cn } from './classes';

/**
 * Allows for variable definitions
 */
export function inlineStyles(
  styles: CSSProperties & Record<`--${string}`, string | undefined>,
): CSSProperties {
  return omitUndefined(styles);
}

type Size = string | number;

interface Options {
  bg?: string;
  color?: string;

  borderColor?: string;
  borderWidth?: Size;

  gridColumns?: string;
  gridRows?: string;
  rowGap?: Size;
  columnGap?: Size;

  size?: Size;
  gap?: Size;
  width?: Size;
  minWidth?: Size;
  maxWidth?: Size;
  height?: Size;
  minHeight?: Size;
  maxHeight?: Size;

  className?: ClassValue | ClassValue[];
  style?: CSSProperties;
}

type Property = Exclude<keyof Options, 'className' | 'style'>;

export function parseSize(size: Size): string {
  return typeof size === 'number' ? `${size}px` : size;
}

const converters: {
  [Prop in Property]: (value: NonNullable<Options[Prop]>) => {
    className: string;
    value: any;
    variable: string;
  };
} = {
  bg: (value) => ({
    className: 'bg-[var(--dynamic-tw-bg)]',
    variable: '--dynamic-tw-bg',
    value: resolveColor(value),
  }),
  color: (value) => ({
    className: 'text-[var(--dynamic-tw-color)]',
    variable: '--dynamic-tw-color',
    value: resolveColor(value),
  }),
  borderColor: (value) => ({
    className: 'border-[var(--dynamic-tw-border-color)]',
    variable: '--dynamic-tw-border-color',
    value: resolveColor(value),
  }),
  size: (value) => ({
    className: 'size-[var(--dynamic-tw-size)]',
    variable: '--dynamic-tw-size',
    value: parseSize(value),
  }),
  borderWidth: (value) => ({
    className: '[border-width:var(--dynamic-tw-border-width)]',
    variable: '--dynamic-tw-border-width',
    value: parseSize(value),
  }),
  gridColumns: (value) => ({
    className: 'grid-cols-[var(--dynamic-tw-grid-columns)]',
    variable: '--dynamic-tw-grid-columns',
    value: parseSize(value),
  }),
  gridRows: (value) => ({
    className: 'grid-rows-[var(--dynamic-tw-grid-rows)]',
    variable: '--dynamic-tw-grid-rows',
    value: parseSize(value),
  }),
  gap: (value) => ({
    className: 'gap-[var(--dynamic-tw-gap)]',
    variable: '--dynamic-tw-gap',
    value: parseSize(value),
  }),
  columnGap: (value) => ({
    className: 'gap-x-[var(--dynamic-tw-column-gap)]',
    variable: '--dynamic-tw-column-gap',
    value: parseSize(value),
  }),
  rowGap: (value) => ({
    className: 'gap-y-[var(--dynamic-tw-row-gap)]',
    variable: '--dynamic-tw-row-gap',
    value: parseSize(value),
  }),
  width: (value) => ({
    className: 'w-[var(--dynamic-tw-width)]',
    variable: '--dynamic-tw-width',
    value: parseSize(value),
  }),
  maxWidth: (value) => ({
    className: 'w-[var(--dynamic-tw-max-width)]',
    variable: '--dynamic-tw-width',
    value: parseSize(value),
  }),
  minWidth: (value) => ({
    className: 'w-[var(--dynamic-tw-min-width)]',
    variable: '--dynamic-tw-width',
    value: parseSize(value),
  }),
  height: (value) => ({
    className: 'h-[var(--dynamic-tw-height)]',
    variable: '--dynamic-tw-height',
    value: parseSize(value),
  }),
  maxHeight: (value) => ({
    className: 'h-[var(--dynamic-tw-max-height)]',
    variable: '--dynamic-tw-height',
    value: parseSize(value),
  }),
  minHeight: (value) => ({
    className: 'h-[var(--dynamic-tw-min-height)]',
    variable: '--dynamic-tw-height',
    value: parseSize(value),
  }),
};

export function dynamicStyling({ className, style = {}, ...dynamicProps }: Options) {
  const validEntries = Object.entries(dynamicProps).filter(([, value]) => value != null);

  const { classes, styles } = validEntries.reduce(
    (acc, [prop, preValue]) => {
      const converter = converters[prop as Property];

      if (!converter) return acc;

      const { className, value, variable } = converter(preValue as any);

      return {
        classes: [...acc.classes, className],

        styles: {
          ...acc.styles,

          [variable]: value,
        },
      };
    },
    { classes: [] as string[], styles: {} },
  );

  return {
    style: {
      ...styles,
      ...style,
    },

    className: cn(...classes, ...ensureArray(className)),
  };
}
